// copy-register: commercial
import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { Glyph, hasGlyph } from "@apf/design-system/glyphs";
import {
  designSystemCatalogue,
  type DesignSystemCatalogueComponent
} from "@apf/design-system/catalogue";
import { useCopy, useFramework } from "../framework";
import { CopyAssetButton, useAssetManifest } from "../downloadsManifest";
import { assetHref } from "../routerMount";
import { CapabilityMapping } from "../components/CapabilityMapping";
import { CatalogueFacet, ContextRecordPreview } from "../components/catalogue";
import {
  AbsenceBlock,
  ArrowLinkLabel,
  BeforeAfter,
  Card,
  Door,
  EmptyState,
  FactBand,
  FactRow,
  Figure,
  GroundworksMark,
  LoadingState,
  Lockup,
  PageHeader,
  PlaceLine,
  SectionHeading,
  StatementSection,
  StatusMark,
  StatusLegend,
  Trail,
  ordinal
} from "../components/ui";

function useFocusedHashTarget(): void {
  const { hash } = useLocation();
  useEffect(() => {
    const focusTarget = (nextHash: string) => {
      if (!nextHash) return;
      let id: string;
      try {
        id = decodeURIComponent(nextHash.slice(1));
      } catch {
        return;
      }
      const target = document.getElementById(id);
      if (!target) return;
      if (typeof target.scrollIntoView === "function") target.scrollIntoView();
      target.focus({ preventScroll: true });
    };

    focusTarget(hash);
    const handleHashChange = () => focusTarget(window.location.hash);
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [hash]);
}

function AnchoredSection({
  id,
  title,
  description,
  disclosureLabel,
  defaultOpen,
  children
}: {
  id: string;
  title: string;
  description?: string;
  disclosureLabel?: string;
  /** Render the disclosure open on arrival; the reader can still fold it. */
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  return (
    <section
      className="apf-page-section apf-design-section"
      id={id}
      tabIndex={-1}
      aria-labelledby={`${id}-title`}
    >
      <SectionHeading id={`${id}-title`} title={title} description={description} />
      {disclosureLabel ? (
        <details className="apf-design-reading" open={defaultOpen}>
          <summary>{disclosureLabel}</summary>
          <div className="apf-design-reading__body">{children}</div>
        </details>
      ) : children}
    </section>
  );
}

/** The public design language behind Accenture GroundWorks framework surfaces. */
export function DesignPage() {
  const copy = useCopy();
  const { snapshot } = useFramework();
  const { anchors, principles, componentCatalogue } = snapshot.designNarrative;
  const admittedIds = new Set(componentCatalogue.componentIds);
  const catalogueComponents = designSystemCatalogue.components.filter(({ id }) => admittedIds.has(id));
  useFocusedHashTarget();

  const sectionBodies: Record<string, ReactNode> = {
    intent: (
      <div className="apf-design-anchor" id="intent" tabIndex={-1}>
        <StatementSection
          eyebrow={copy("page.design.intent.eyebrow")}
          headline={copy("page.design.intent.headline")}
          inverse
          treatment="gradient"
        >
          <p>{copy("page.design.intent.body")}</p>
        </StatementSection>
        <DesignShowcaseFrame components={catalogueComponents} copy={copy} />
      </div>
    ),
    principles: (
      <AnchoredSection
        id="principles"
        title={copy("page.design.principles.title")}
        description={copy("page.design.principles.description")}
        disclosureLabel={copy("page.design.read-more")}
        defaultOpen
      >
        <ol className="apf-design-principles">
          {principles.map((principle, index) => (
            <li key={principle.id}>
              <span className="apf-design-principles__marker" aria-hidden="true">
                {ordinal(index + 1)}
              </span>
              <div>
                <h3>
                  {hasGlyph(principle.glyphMeaning) ? <Glyph meaning={principle.glyphMeaning} size={20} /> : null}
                  {copy(principle.titleKey)}
                </h3>
                <p>{copy(principle.bodyKey)}</p>
              </div>
            </li>
          ))}
        </ol>
      </AnchoredSection>
    ),
    "groundworks-choices": (
      <AnchoredSection
        id="groundworks-choices"
        title={copy("page.design.choices.title")}
        description={copy("page.design.choices.description")}
        disclosureLabel={copy("page.design.read-more")}
      >
        <ol className="apf-design-choices">
          {principles.map((principle, index) => (
            <li key={principle.id}>
              <span>{ordinal(index + 1)}</span>
              <div>
                <h3>{copy(principle.choiceTitleKey)}</h3>
                <p>{copy(principle.choiceBodyKey)}</p>
              </div>
            </li>
          ))}
        </ol>
      </AnchoredSection>
    ),
    specimens: (
      <AnchoredSection
        id="specimens"
        title={copy("page.design.specimens.title")}
        description={copy("page.design.specimens.description")}
      >
        <FactBand
          label={copy("page.design.specimens.title")}
          facts={designSystemCatalogue.categories.map((category) => ({
            label: copy(category.labelKey),
            reading: catalogueComponents.filter(({ categoryId }) => categoryId === category.id).length
          }))}
        />
        <nav className="apf-design-catalogue-index" aria-label={copy("page.design.catalogue.index-label")}>
          <ol>
            {designSystemCatalogue.categories.map((category, index) => {
              const count = catalogueComponents.filter(({ categoryId }) => categoryId === category.id).length;
              if (!count) return null;
              return (
                <li key={category.id}>
                  <a href={`#design-category-${category.id}`}>
                    <span aria-hidden="true">{ordinal(index + 1)}</span>
                    <span>{copy(category.labelKey)}</span>
                    <span>{count}</span>
                  </a>
                </li>
              );
            })}
          </ol>
        </nav>
        <div className="apf-design-catalogue">
          {designSystemCatalogue.categories.map((category) => {
            const components = catalogueComponents.filter(({ categoryId }) => categoryId === category.id);
            if (!components.length) return null;
            return (
              <section className="apf-design-category" aria-labelledby={`design-category-${category.id}`} key={category.id}>
                <header className="apf-design-category__header">
                  <div>
                    <h3 id={`design-category-${category.id}`} tabIndex={-1}>{copy(category.labelKey)}</h3>
                    <p>{copy(category.descriptionKey)}</p>
                  </div>
                  <span className="apf-design-category__count">{formatCount(copy("page.design.catalogue.component-count"), components.length)}</span>
                </header>
                <div className="apf-design-component-grid">
                  {components.map((component) => <ComponentReference component={component} key={component.id} />)}
                </div>
              </section>
            );
          })}
        </div>
      </AnchoredSection>
    ),
    provenance: (
      <AnchoredSection
        id="provenance"
        title={copy("page.design.provenance.title")}
        description={copy("page.design.provenance.description")}
        disclosureLabel={copy("page.design.read-more")}
      >
        <ol className="apf-design-lineage">
          {(["knowledge", "groundworks", "framework", "accenture"] as const).map((step, index) => (
            <li key={step}>
              <span>{ordinal(index + 1)}</span>
              <div>
                <h3>{copy(`page.design.provenance.${step}.title`)}</h3>
                <p>{copy(`page.design.provenance.${step}.body`)}</p>
              </div>
            </li>
          ))}
        </ol>
      </AnchoredSection>
    ),
    accessibility: (
      <AnchoredSection
        id="accessibility"
        title={copy("page.design.accessibility.title")}
        description={copy("page.design.accessibility.description")}
        disclosureLabel={copy("page.design.read-more")}
      >
        <ul className="apf-design-checks">
          {(["structure", "keyboard", "meaning", "reading"] as const).map((item) => (
            <li key={item}>
              <Glyph meaning="utility.check" size={16} />
              <span>{copy(`page.design.accessibility.${item}`)}</span>
            </li>
          ))}
        </ul>
      </AnchoredSection>
    ),
    "responsive-behaviour": (
      <AnchoredSection
        id="responsive-behaviour"
        title={copy("page.design.responsive.title")}
        description={copy("page.design.responsive.description")}
        disclosureLabel={copy("page.design.read-more")}
      >
        <BeforeAfter
          title={copy("page.design.responsive.comparison-title")}
          beforeLabel={copy("page.design.responsive.wide-label")}
          afterLabel={copy("page.design.responsive.narrow-label")}
          before={copy("page.design.responsive.wide-body")}
          after={copy("page.design.responsive.narrow-body")}
        />
      </AnchoredSection>
    ),
    "present-scope": (
      <AnchoredSection
        id="present-scope"
        title={copy("page.design.scope.title")}
        description={copy("page.design.scope.description")}
        disclosureLabel={copy("page.design.read-more")}
      >
        <p className="apf-design-scope">{copy("page.design.scope.body")}</p>
      </AnchoredSection>
    ),
    continue: (
      <AnchoredSection
        id="continue"
        title={copy("page.design.continue.title")}
        description={copy("page.design.continue.description")}
      >
        <Trail
          label={copy("page.design.continue.trail-label")}
          items={[
            {
              to: "/",
              glyph: "utility.map",
              label: copy("page.design.continue.framework.label"),
              context: copy("page.design.continue.framework.context")
            },
            {
              to: "/workblocks",
              glyph: "class.business-module",
              label: copy("page.design.continue.workblocks.label"),
              context: copy("page.design.continue.workblocks.context")
            },
            {
              to: "/architecture",
              glyph: "capability.integration",
              label: copy("page.design.continue.platform.label"),
              context: copy("page.design.continue.platform.context")
            }
          ]}
        />
      </AnchoredSection>
    )
  };

  return (
    <div className="apf-page apf-register-marketing apf-design-page">
      <PageHeader
        eyebrow={copy("page.design.eyebrow")}
        title={copy("page.design.title")}
        description={copy("page.design.description")}
      />

      <nav className="apf-design-contents" aria-label={copy("page.design.contents-label")}>
        <ol>
          {anchors.map((anchor, index) => (
            <li key={anchor.id}>
              <a href={`#${anchor.id}`}>
                <span aria-hidden="true">{ordinal(index + 1)}</span>
                {copy(anchor.labelKey)}
              </a>
            </li>
          ))}
        </ol>
      </nav>
      {anchors.map(({ id }) => <div className="apf-design-section-slot" key={id}>{sectionBodies[id]}</div>)}
    </div>
  );
}

function formatCount(template: string, count: number): string {
  return template.replaceAll("{count}", String(count));
}

function formatLabel(template: string, label: string): string {
  return template.replaceAll("{label}", label);
}

type DesignCopy = (key: string, fallback?: string) => string;

function DesignShowcaseFrame({
  components,
  copy
}: {
  components: DesignSystemCatalogueComponent[];
  copy: DesignCopy;
}) {
  /* The evidence band reads the governed snapshot; a hardcoded 5/9/45 would
   * silently lie the day the framework grows. */
  const { snapshot } = useFramework();
  const modules = snapshot.businessModuleRegistry.businessModules;
  /* The whole system ships as one governed markdown file; the manifest names
   * its published path, and the frame never fabricates one. */
  const { manifest } = useAssetManifest();
  const doctrineFormat = manifest?.assets
    .find((asset) => asset.assetClass === "design-doctrine")
    ?.formats.find((format) => format.format === "markdown");
  const categories = designSystemCatalogue.categories.map((category) => ({
    ...category,
    components: components.filter(({ categoryId }) => categoryId === category.id)
  })).filter(({ components: entries }) => entries.length);

  return (
    <section className="apf-design-frame" aria-labelledby="design-showcase-frame-title">
      <header className="apf-design-frame__header">
        <PlaceLine
          section={copy("page.design.showcase.place-section")}
          segments={[{ text: formatCount(copy("page.design.catalogue.component-count"), components.length) }, { text: formatCount(copy("page.design.showcase.category-count"), categories.length) }]}
          cut={copy("page.design.showcase.place-cut")}
        />
        <div className="apf-design-frame__statement">
          <div>
            <p className="apf-design-frame__eyebrow">{copy("page.design.showcase.eyebrow")}</p>
            <h2 id="design-showcase-frame-title">{copy("page.design.showcase.title")}</h2>
            <p>{copy("page.design.showcase.description")}</p>
          </div>
          <FactRow
            facts={[
              { label: copy("page.design.showcase.live-label"), value: components.filter(({ preview }) => preview.mode === "live").length },
              { label: copy("page.design.showcase.categories-label"), value: categories.length },
              { label: copy("page.design.showcase.register-label"), value: new Set(components.map(({ register }) => register)).size }
            ]}
          />
        </div>
        {doctrineFormat ? (
          <div className="apf-design-frame__doctrine">
            <a className="apf-button apf-button--secondary" href={assetHref(doctrineFormat.path)} download>
              {copy("page.design.doctrine.download", "Download the app design doctrine (design.md)")}
            </a>
            <CopyAssetButton path={doctrineFormat.path} copy={copy} />
          </div>
        ) : null}
      </header>
      <nav className="apf-design-frame__index" aria-label={copy("page.design.showcase.index-label")}>
        <ol>
          {categories.map((category, index) => (
            <li key={category.id}>
              <a href={`#design-category-${category.id}`}>
                <span aria-hidden="true">{ordinal(index + 1)}</span>
                <span>{copy(category.labelKey)}</span>
                <span className="apf-design-frame__index-count">{category.components.length}</span>
              </a>
            </li>
          ))}
        </ol>
      </nav>
      <div className="apf-design-frame__body">
        <div className="apf-design-frame__live">
          <SectionHeading
            title={copy("page.design.showcase.live-title")}
            description={copy("page.design.showcase.live-description")}
          />
          <div className="apf-design-frame__live-grid">
            <div className="apf-design-frame__live-item">
              <p className="apf-design-frame__item-label">{copy("page.design.showcase.state-label")}</p>
              <StatusLegend />
            </div>
            <div className="apf-design-frame__live-item">
              <p className="apf-design-frame__item-label">{copy("page.design.showcase.evidence-label")}</p>
              <FactBand
                label={copy("page.design.showcase.evidence-label")}
                facts={[
                  { label: copy("page.design.showcase.lifecycle-label"), reading: (snapshot.content.stages ?? []).length },
                  { label: copy("page.design.showcase.workblock-label"), reading: snapshot.content.domainWorkblocks.length },
                  { label: copy("page.design.showcase.module-label"), reading: modules.length }
                ]}
              />
            </div>
            <div className="apf-design-frame__live-item apf-design-frame__live-item--wide">
              <BeforeAfter
                title={copy("page.design.showcase.change-title")}
                beforeLabel={copy("page.design.showcase.change-before-label")}
                afterLabel={copy("page.design.showcase.change-after-label")}
                before={copy("page.design.showcase.change-before")}
                after={copy("page.design.showcase.change-after")}
              />
            </div>
            <div className="apf-design-frame__live-item apf-design-frame__live-item--wide">
              <CapabilityMapping
                label={copy("page.design.showcase.mapping-label")}
                items={[
                  { id: "sourcing", label: copy("page.design.showcase.mapping-sourcing"), context: copy("page.design.showcase.mapping-workblock"), fit: "direct", fitLabel: copy("page.design.showcase.mapping-direct"), to: "/workblocks/sourcing-negotiation" },
                  { id: "connected", label: copy("page.design.showcase.mapping-connected"), context: copy("page.design.showcase.mapping-recipe"), fit: "partial", fitLabel: copy("page.design.showcase.mapping-partial"), to: "/connected-work" }
                ]}
              />
            </div>
          </div>
        </div>
        <aside className="apf-design-frame__contract" aria-labelledby="design-showcase-contract-title">
          <p className="apf-design-frame__item-label">{copy("page.design.showcase.contract-label")}</p>
          <h3 id="design-showcase-contract-title">{copy("page.design.showcase.contract-title")}</h3>
          <p>{copy("page.design.showcase.contract-body")}</p>
          <ul>
            <li>{copy("page.design.showcase.contract-meaning")}</li>
            <li>{copy("page.design.showcase.contract-state")}</li>
            <li>{copy("page.design.showcase.contract-authority")}</li>
          </ul>
        </aside>
      </div>
    </section>
  );
}

function ComponentReference({ component }: { component: DesignSystemCatalogueComponent }) {
  const copy = useCopy();
  return (
    <article className="apf-design-component" id={`component-${component.id}`}>
      {/* The copy controls sit beside the artifacts they copy, inside the
        * disclosure, so the header stays a header and the reader copies from
        * where they read. */}
      <header className="apf-design-component__header">
        <div>
          <p className="apf-design-component__register">{component.register}</p>
          <h4>{component.exportName}</h4>
        </div>
      </header>
      <p className="apf-design-component__purpose">{component.purpose}</p>
      <div className="apf-design-component__preview">
        {component.preview.mode === "live" ? <LiveComponentPreview fixture={component.preview.fixture} /> : null}
        {component.preview.mode === "current-surface" ? (
          <p className="apf-design-component__context-note">
            <Glyph meaning="utility.check" size={16} />
            {copy("page.design.catalogue.current-surface")}
          </p>
        ) : null}
        {component.preview.mode === "linked" ? (
          <div className="apf-design-component__context-note">
            <p>{copy("page.design.catalogue.linked")}</p>
            <Link to={component.preview.route}>
              <ArrowLinkLabel>{copy("page.design.catalogue.open-example")}</ArrowLinkLabel>
            </Link>
          </div>
        ) : null}
      </div>
      <details className="apf-design-reference-artifacts">
        <summary>{copy("page.design.catalogue.reference-artifacts")}</summary>
        <div className="apf-design-reference-artifacts__body">
          <section>
            <div className="apf-design-artifact-header">
              <h5>{copy("page.design.catalogue.reference-code")}</h5>
              <CopyButton kind="code" value={stampedCodeArtifact(component)} copy={copy} />
            </div>
            <pre tabIndex={0}><code>{component.code}</code></pre>
          </section>
          <section>
            <div className="apf-design-artifact-header">
              <h5>{copy("page.design.catalogue.design-prompt")}</h5>
              <CopyButton kind="prompt" value={stampedPromptArtifact(component)} copy={copy} />
            </div>
            <pre tabIndex={0}><code>{component.prompt}</code></pre>
          </section>
        </div>
      </details>
      <ComponentContractDetails component={component} />
    </article>
  );
}

type CopyKind = "code" | "prompt";
type CopyState = "idle" | "copied" | "error";

function assetProvenanceLine(component: DesignSystemCatalogueComponent, hash: string): string {
  const { asset } = component;
  return `${asset.assetId} v${asset.version} · component registry ${asset.registryVersion} · sha256 ${hash.slice(0, 12)}`;
}

function stampedCodeArtifact(component: DesignSystemCatalogueComponent): string {
  return `// ${assetProvenanceLine(component, component.asset.codeHash)}\n${component.code}`;
}

function stampedPromptArtifact(component: DesignSystemCatalogueComponent): string {
  return `${component.prompt}\nProvenance: ${assetProvenanceLine(component, component.asset.promptHash)}`;
}

function CopyButton({ kind, value, copy }: { kind: CopyKind; value: string; copy: DesignCopy }) {
  const [state, setState] = useState<CopyState>("idle");

  useEffect(() => setState("idle"), [value]);

  const handleCopy = async () => {
    try {
      if (globalThis.navigator?.clipboard?.writeText) {
        await globalThis.navigator.clipboard.writeText(value);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = value;
        textarea.setAttribute("readonly", "true");
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        try {
          textarea.select();
          if (!document.execCommand("copy")) throw new Error("Clipboard unavailable");
        } finally {
          /* The helper element never survives a failed copy. */
          textarea.remove();
        }
      }
      setState("copied");
    } catch {
      setState("error");
    }
  };

  const labelKey = state === "copied"
    ? `page.design.catalogue.copied-${kind}`
    : state === "error"
      ? "page.design.catalogue.copy-failed"
      : `page.design.catalogue.copy-${kind}`;
  return (
    <button className="apf-button apf-button--quiet apf-design-copy-button" data-copy-state={state} type="button" onClick={handleCopy}>
      {state === "copied" ? <Glyph meaning="utility.check" size={16} /> : null}
      <span aria-live="polite">{copy(labelKey)}</span>
    </button>
  );
}

function ComponentContractDetails({ component }: { component: DesignSystemCatalogueComponent }) {
  const copy = useCopy();
  const states = Object.entries(component.states);
  return (
    <details className="apf-design-contract">
      <summary>{copy("page.design.catalogue.contract-summary")}</summary>
      <div className="apf-design-contract__body">
        <section>
          <h5>{copy("page.design.catalogue.anatomy")}</h5>
          <dl>
            {component.anatomy.map((part) => (
              <div key={part.part}><dt>{part.part}</dt>{part.carries ? <dd>{part.carries}</dd> : null}</div>
            ))}
          </dl>
        </section>
        {component.variants.length ? (
          <section>
            <h5>{copy("page.design.catalogue.variants")}</h5>
            <dl>
              {component.variants.map((variant) => (
                <div key={variant.name}><dt>{variant.name}</dt><dd>{variant.meaning}</dd></div>
              ))}
            </dl>
          </section>
        ) : null}
        <section>
          <h5>{copy("page.design.catalogue.states")}</h5>
          <dl>
            {states.map(([name, meaning]) => (
              <div key={name}><dt>{name}</dt><dd>{meaning}</dd></div>
            ))}
          </dl>
        </section>
        <section>
          <h5>{copy("page.design.catalogue.accessibility")}</h5>
          <ul>{component.keyboardAndAssistiveBehavior.map((item) => <li key={item}>{item}</li>)}</ul>
        </section>
        <section>
          <h5>{copy("page.design.catalogue.failure")}</h5>
          <ul>{component.failureBehavior.map((item) => <li key={item}>{item}</li>)}</ul>
        </section>
      </div>
    </details>
  );
}

type PreviewFixture = (copy: DesignCopy) => ReactNode;

/**
 * The showcase registry names fixtures; this map owns only the React wiring.
 * Adding a component therefore means adding one fixture entry to showcase.json
 * and one deliberately bounded specimen here, rather than extending a page
 * switch or scattering component IDs through the route.
 */
const liveComponentFixtures: Record<string, PreviewFixture> = {
  glyph: () => <div className="apf-design-glyph-row"><Glyph meaning="class.record" size={24} /><Glyph meaning="class.role" size={24} /><Glyph meaning="class.job" size={24} /><Glyph meaning="object.workblock" size={24} /></div>,
  "groundworks-mark": () => <GroundworksMark size={40} />,
  lockup: () => <Lockup mark />,
  "place-line": (copy) => <PlaceLine section={copy("page.design.specimen.place-line.section", "Design")} segments={[{ text: copy("page.design.specimen.place-line.segment", "26 components") }]} cut={copy("page.design.specimen.place-line.cut", "Canonical")} />,
  trail: (copy) => <Trail label={copy("page.design.specimen.trail.label", "Specimen destinations")} items={[{ to: "/", glyph: "utility.map", label: copy("page.design.specimen.trail.framework-label", "Framework"), context: copy("page.design.specimen.trail.framework-context", "Return to the framework map.") }, { to: "/reference", glyph: "class.record", label: copy("page.design.specimen.trail.reference-label", "Reference"), context: copy("page.design.specimen.trail.reference-context", "Open the governed reference.") }]} />,
  door: (copy) => <div className="apf-door-list"><Door to="/reference" glyph="class.record" index="01" title={copy("page.design.specimen.door.title", "Open the reference")} line={copy("page.design.specimen.door.line", "Follow the records, language and design contracts behind the framework.")} meta={copy("page.design.specimen.door.meta", "7 categories")} /></div>,
  "arrow-link-label": (copy) => <Link to="/reference"><ArrowLinkLabel>{copy("page.design.specimen.arrow-link-label.text", "Open the reference")}</ArrowLinkLabel></Link>,
  "status-mark": () => <div className="apf-design-status-row"><StatusMark value="reference" /><StatusMark value="pattern" /><StatusMark value="roadmap" /></div>,
  "status-legend": () => <StatusLegend />,
  "empty-state": (copy) => <EmptyState title={copy("page.design.specimen.empty-state.title", "No matching components")} description={copy("page.design.specimen.empty-state.description", "Clear the active filters to return to the complete catalogue.")} />,
  "absence-block": (copy) => <AbsenceBlock eyebrow={copy("page.design.specimen.absence-block.eyebrow", "Reference absence")} title={copy("page.design.specimen.absence-block.title", "That component is not in this release.")} support={copy("page.design.specimen.absence-block.support", "The complete admitted catalogue remains available.")} holds={[{ label: copy("page.design.specimen.absence-block.hold-label", "Admitted components"), count: 26 }]} routes={<Link to="#specimens">{copy("page.design.specimen.absence-block.route", "Return to all components")}</Link>} />,
  "loading-state": () => <LoadingState />,
  "fact-band": (copy) => <FactBand label={copy("page.design.specimen.fact-band.label", "Framework structure")} facts={[{ label: copy("page.design.specimen.fact-band.stage-label", "Lifecycle stages"), reading: 5 }, { label: copy("page.design.specimen.fact-band.workblock-label", "Domain Workblocks"), reading: 9 }, { label: copy("page.design.specimen.fact-band.module-label", "Business modules"), reading: 45 }]} />,
  "fact-row": (copy) => <FactRow facts={[{ label: copy("page.design.specimen.fact-row.categories-label", "categories"), value: 6 }, { label: copy("page.design.specimen.fact-row.components-label", "components"), value: 26 }, { label: copy("page.design.specimen.fact-row.registers-label", "registers"), value: 2 }]} />,
  figure: (copy) => <Figure lead={copy("page.design.specimen.figure.lead", "The catalogue in one reading")}><FactRow facts={[{ label: copy("page.design.specimen.figure.components-label", "components"), value: 26 }, { label: copy("page.design.specimen.figure.categories-label", "categories"), value: 6 }]} /></Figure>,
  "before-after": (copy) => <BeforeAfter title={copy("page.design.specimen.before-after.title", "From inventory to reference")} beforeLabel={copy("page.design.specimen.before-after.before-label", "Before")} afterLabel={copy("page.design.specimen.before-after.after-label", "GroundWorks")} before={copy("page.design.specimen.before-after.before", "Four isolated examples leave most component contracts hidden.")} after={copy("page.design.specimen.before-after.after", "One generated catalogue carries every admitted component and its options.")} />,
  "statement-section": (copy) => <StatementSection eyebrow={copy("page.design.specimen.statement-section.eyebrow", "Design contract")} headline={copy("page.design.specimen.statement-section.headline", "Meaning stays stable while expression changes.")}><p>{copy("page.design.specimen.statement-section.body", "Components bind semantic roles and retain one purpose across themes and containers.")}</p></StatementSection>,
  card: (copy) => <ul className="apf-card-grid"><Card to="/design" glyph="utility.grid" index="01" name={copy("page.design.specimen.card.name", "Design")} descriptor={copy("page.design.specimen.card.descriptor", "The interface language")} explainer={copy("page.design.specimen.card.explainer", "Browse every admitted component and its contract.")} action={copy("page.design.specimen.card.action", "26 components")} /></ul>,
  "context-record-preview": () => <ContextRecordPreviewSpecimen />,
  "catalogue-facet": () => <CatalogueFacetSpecimen />,
  "capability-mapping": (copy) => <CapabilityMapping label={copy("page.design.specimen.capability-mapping.label", "Related framework capabilities")} items={[{ id: "sourcing", label: copy("page.design.specimen.capability-mapping.sourcing-label", "Sourcing and negotiation"), context: copy("page.design.specimen.capability-mapping.sourcing-context", "Domain Workblock"), fit: "direct", fitLabel: copy("page.design.specimen.capability-mapping.direct-fit", "Direct fit"), to: "/workblocks/sourcing-negotiation" }, { id: "connected", label: copy("page.design.specimen.capability-mapping.connected-label", "Connected Work"), context: copy("page.design.specimen.capability-mapping.connected-context", "Integration recipes"), fit: "partial", fitLabel: copy("page.design.specimen.capability-mapping.partial-fit", "Partial fit"), to: "/connected-work" }]} />
};

function LiveComponentPreview({ fixture }: { fixture: string }): ReactNode {
  const copy = useCopy();
  return liveComponentFixtures[fixture]?.(copy) ?? null;
}

function ContextRecordPreviewSpecimen() {
  const copy = useCopy();
  const [expanded, setExpanded] = useState(false);
  return (
    <ContextRecordPreview
      id="design-role-preview"
      variant="role"
      heading={copy("page.design.specimen.context-record-preview.heading", "Category manager")}
      summary={copy("page.design.specimen.context-record-preview.summary", "Owns category strategy, sourcing outcomes and supplier performance.")}
      labels={{
        showDetails: copy("page.design.specimen.context-record-preview.show-details", "Show context"),
        hideDetails: copy("page.design.specimen.context-record-preview.hide-details", "Hide context")
      }}
      expanded={expanded}
      onExpandedChange={setExpanded}
      canonicalLinkLabel={copy("page.design.specimen.context-record-preview.canonical-link", "Open role")}
      to="/reference/roles"
      metadata={[{
        label: copy("page.design.specimen.context-record-preview.metadata-label", "Domain"),
        value: copy("page.design.specimen.context-record-preview.metadata-value", "Sourcing and negotiation")
      }]}
      detail={<p>{copy("page.design.specimen.context-record-preview.detail", "Reviews proposals and approves the sourcing decision inside the declared authority boundary.")}</p>}
    />
  );
}

function CatalogueFacetSpecimen() {
  const copy = useCopy();
  const [open, setOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>(["framework"]);
  return (
    <CatalogueFacet
      id="design-register"
      label={copy("page.design.specimen.catalogue-facet.label", "Register")}
      labels={{
        search: (label) => formatLabel(copy("page.design.specimen.catalogue-facet.search", "Search {label}"), label.toLowerCase()),
        clear: (label) => formatLabel(copy("page.design.specimen.catalogue-facet.clear", "Clear {label}"), label.toLowerCase()),
        clearAll: copy("page.design.specimen.catalogue-facet.clear-all", "Clear all"),
        selectedCount: (count) => formatCount(copy("page.design.specimen.catalogue-facet.selected-count", "{count} selected"), count),
        moreCount: (count) => formatCount(copy("page.design.specimen.catalogue-facet.more-count", "{count} more"), count),
        noOptions: copy("page.design.specimen.catalogue-facet.no-options", "No registers match"),
        tryAgain: copy("page.design.specimen.catalogue-facet.try-again", "Try again"),
        resultCount: (count) => formatCount(copy("page.design.specimen.catalogue-facet.result-count", "{count} results"), count),
        optionCount: (count) => formatCount(copy("page.design.specimen.catalogue-facet.option-count", "{count} components"), count),
        unavailableCount: (count) => formatCount(copy("page.design.specimen.catalogue-facet.unavailable-count", "{count} unavailable"), count),
        clearUnavailable: copy("page.design.specimen.catalogue-facet.clear-unavailable", "Clear unavailable")
      }}
      options={[
        { id: "framework", label: copy("page.design.specimen.catalogue-facet.option-framework", "Framework"), count: 25 },
        { id: "shared", label: copy("page.design.specimen.catalogue-facet.option-shared", "Shared"), count: 1 }
      ]}
      selectedIds={selectedIds}
      onSelectedIdsChange={setSelectedIds}
      open={open}
      onOpenChange={setOpen}
      onClearAll={() => setSelectedIds([])}
    />
  );
}
